import { Location } from 'history';
import * as steps from 'const/paths';
import { PathBankStatements, PathsDocuments } from 'const/paths';
import {
  IAppSettings,
  IBankStatementAnalysis,
  IUserInfo,
  UserState,
} from 'redux/reducers/user/type';
import { StateLocationType } from 'types';
import { AADHAAR_VERIFICATION_ENGINES } from '../../const';

type Fields = Array<keyof IUserInfo>;

interface IGetIsStep {
  userInfo: IUserInfo;
  fields: Fields;
  location: Location<StateLocationType>;
  BankStatementAnalysis?: IBankStatementAnalysis;
  appSettings?: IAppSettings | null;
}

interface IRegistrationSteps {
  getIsStep: (data: IGetIsStep) => boolean;
  currentPath: string;
  fields: Fields;
}

export enum StepIdx {
  One = 1,
  Two = 2,
  TwentyOne = 21,
  Three = 3,
  Four = 4,
  Five = 5,
  Six = 6,
  SixtyOne = 61,
  Seven = 7,
  SeventyOne = 71,
  SeventyTwo = 72,
  Eight = 8,
  Ninth = 9,
  Tenth = 10,
}

// /reg1 'enter phone number'
const getFirstStep = ({ userInfo }: IGetIsStep) => !userInfo.id;

// /reg2 'Enter OTP'
const getSecondStep = ({ userInfo }: IGetIsStep) =>
  userInfo.registration_step === StepIdx.One && userInfo.state === UserState.CREATED;

// /reg2/confirm-terms 'Confirm Terms'
const getSecondOneStep = ({ userInfo }: IGetIsStep) =>
  userInfo.state === UserState.CONFIRMED &&
  !!userInfo.is_data_processing_consent_given &&
  !userInfo.is_credit_bureau_consent_given;

// /reg3 'Personal Info'
const getThirdStep = ({ userInfo }: IGetIsStep) =>
  userInfo.state === UserState.CONFIRMED &&
  !!userInfo.is_data_processing_consent_given &&
  !!userInfo.is_credit_bureau_consent_given &&
  !userInfo.first_name;

// /reg4 'Employment details'
const getFourthStep = ({ userInfo }: IGetIsStep) =>
  !!userInfo.first_name && !userInfo.employment_type;

// /reg5 'Additional info'
const getFifthStep = ({ userInfo }: IGetIsStep) =>
  !!userInfo.employment_type && !userInfo.contact_phone_number;

// /documents/aadhaar_verification
const getSixthStep = ({ userInfo, location }: IGetIsStep) =>
  !!userInfo.contact_phone_number &&
  !userInfo.registration_address &&
  (!userInfo.aadhaar_number || !location.state?.pathDocuments);

// /documents/aadhaar_otp
const getSixtyOneStep = ({ userInfo, location, appSettings }: IGetIsStep) =>
  !!userInfo.contact_phone_number &&
  location.state?.pathDocuments === PathsDocuments.AADHAAR_OTP &&
  !userInfo.registration_address &&
  appSettings?.external?.aadhaar_verification?.engine !== AADHAAR_VERIFICATION_ENGINES.DIGILOCKER;

// penny transfer
const getSeventhStep = ({ location, userInfo }: IGetIsStep) =>
  !!userInfo.registration_address && !userInfo.ifsc_code && !location.state?.pathBankStatements;

// finbit upload statement
const getSeventyOneStep = ({ location, userInfo }: IGetIsStep) =>
  !!userInfo.registration_address &&
  !userInfo.ifsc_code &&
  location.state.pathBankStatements === PathBankStatements.UPLOAD;

// bank_statement_analysis
const getSeventyTwoStep = ({ location, userInfo }: IGetIsStep) =>
  !!userInfo.bank_account_number &&
  !!userInfo.ifsc_code &&
  location.state?.pathBankStatements === PathBankStatements.SETUP;

// /experian
const getEightStep = ({ userInfo }: IGetIsStep) =>
  !!userInfo.ifsc_code && !!userInfo.bank_account_number;

// /upload_photo
const getNinthStep = ({ userInfo }: IGetIsStep) =>
  !!userInfo.ifsc_code && !userInfo.is_approved_photo;

// /Confirmation Conditions
const getTenthStep = ({ userInfo }: IGetIsStep) => !!userInfo.is_approved_photo;

const registrationSteps = new Map<StepIdx, IRegistrationSteps>();

registrationSteps
  .set(StepIdx.One, {
    getIsStep: getFirstStep,
    currentPath: steps.PATH_REGISTRATION_STEP_1,
    fields: [],
  })

  .set(StepIdx.Two, {
    getIsStep: getSecondStep,
    currentPath: steps.PATH_REGISTRATION_STEP_2,
    fields: [],
  })

  .set(StepIdx.TwentyOne, {
    getIsStep: getSecondOneStep,
    currentPath: steps.PATH_CONFIRM_TERMS,
    fields: [],
  })

  .set(StepIdx.Three, {
    getIsStep: getThirdStep,
    currentPath: steps.PATH_REGISTRATION_STEP_3,
    fields: [],
  })

  .set(StepIdx.Four, {
    getIsStep: getFourthStep,
    currentPath: steps.PATH_REGISTRATION_STEP_4,
    fields: [],
  })

  .set(StepIdx.Five, {
    getIsStep: getFifthStep,
    currentPath: steps.PATH_REGISTRATION_STEP_5,
    fields: [],
  })

  .set(StepIdx.Six, {
    getIsStep: getSixthStep,
    currentPath: steps.PATH_DOCUMENTS_AADHAAR_VERIFICATION,
    fields: ['email'],
  })

  .set(StepIdx.SixtyOne, {
    getIsStep: getSixtyOneStep,
    currentPath: steps.PATH_DOCUMENTS_AADHAAR_OTP,
    fields: ['email'],
  })

  .set(StepIdx.Seven, {
    getIsStep: getSeventhStep,
    currentPath: steps.PATH_REGISTRATION_STEP_7,
    fields: ['email'],
  })

  .set(StepIdx.SeventyOne, {
    getIsStep: getSeventyOneStep,
    currentPath: steps.PATH_BANK_STATEMENT_UPLOAD,
    fields: [],
  })

  .set(StepIdx.SeventyTwo, {
    getIsStep: getSeventyTwoStep,
    currentPath: steps.PATH_BANK_STATEMENT_SETUP,
    fields: [],
  })

  .set(StepIdx.Eight, {
    getIsStep: getEightStep,
    currentPath: steps.PATH_REGISTRATION_STEP_8,
    fields: [],
  })

  .set(StepIdx.Ninth, {
    getIsStep: getNinthStep,
    currentPath: steps.PATH_REGISTRATION_STEP_9,
    fields: [],
  })

  .set(StepIdx.Tenth, {
    getIsStep: getTenthStep,
    currentPath: steps.PATH_REGISTRATION_STEP_10,
    fields: [],
  });

export const getStepRegistration = (
  userInfo: IUserInfo,
  location: Location<StateLocationType>,
  appSettings?: IAppSettings | null
): string => {
  let path: string = steps.PATH_REGISTRATION_STEP_1;

  for (let value of registrationSteps.values()) {
    const { getIsStep, currentPath, fields } = value;

    if (getIsStep({ userInfo, fields, location, appSettings })) {
      path = currentPath;
      break;
    }
  }

  return path;
};
